import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

interface InstanceCostMeterProps {
  hourlyCost: number;
  launchTime?: string | null;
}

const InstanceCostMeter: React.FC<InstanceCostMeterProps> = ({ hourlyCost, launchTime }) => {
  const [runningTime, setRunningTime] = useState(0);
  const [currentCost, setCurrentCost] = useState(0);

  useEffect(() => {
    // Calculate runtime from launch time
    const calculateRuntime = () => {
      if (launchTime) {
        const launchDate = new Date(launchTime);
        const now = new Date();
        const seconds = Math.floor((now.getTime() - launchDate.getTime()) / 1000);
        setRunningTime(Math.max(0, seconds));
      }
    };

    // Set initial runtime
    calculateRuntime();

    // Update every second
    const interval = setInterval(calculateRuntime, 1000);

    return () => clearInterval(interval);
  }, [launchTime]);

  const minCharge = hourlyCost * (10 / 60);
  const isUnderMinimum = (runningTime / 3600) * hourlyCost < minCharge;

  useEffect(() => {
    const hoursRun = runningTime / 3600;
    const timeBased = hoursRun * hourlyCost;
    setCurrentCost(Math.max(timeBased, minCharge));
  }, [runningTime, hourlyCost, minCharge]);

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    }
    return `${secs}s`;
  };

  const getCostColor = () => {
    if (currentCost > 10) return '#ef4444'; // red
    if (currentCost > 5) return '#fbbf24'; // yellow
    if (currentCost > 1) return '#60a5fa'; // blue
    return '#10b981'; // green
  };

  return (
    <div className="bg-black/50 backdrop-blur-sm p-2 border border-gray-800/50 h-full flex flex-col shadow-lg">
      <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-0.5">Instance Cost</h3>
      <p className="text-[10px] text-gray-600 mb-1">Shared across all notebooks</p>
      
      <div className="flex-1 flex flex-col justify-center space-y-1">
        {/* Hourly Rate */}
        <div className="text-center">
          <div className="text-xs text-gray-500">Rate</div>
          <div className="text-sm font-bold text-gray-300">
            ${hourlyCost.toFixed(2)}/hr
          </div>
        </div>

        {/* Current Cost */}
        <motion.div 
          className="text-center py-2 border-t border-gray-800/50"
          animate={{ 
            backgroundColor: currentCost > 5 ? 'rgba(251, 191, 36, 0.05)' : 'transparent'
          }}
          transition={{ duration: 0.3 }}
        >
          <div className="text-xs text-gray-500">Current</div>
          <motion.div
            key={currentCost.toFixed(3)}
            initial={{ scale: 0.95 }}
            animate={{ scale: 1 }}
            className="text-lg font-bold"
            style={{ color: getCostColor() }}
          >
            ${currentCost.toFixed(3)}
          </motion.div>
          {isUnderMinimum && (
            <div className="text-[10px] text-gray-500 mt-0.5">10 min minimum</div>
          )}
        </motion.div>

        {/* Running Time */}
        <div className="text-center">
          <div className="text-xs text-gray-500">Runtime</div>
          <div className="text-xs font-mono text-gray-400">
            {formatTime(runningTime)}
          </div>
        </div>

        {/* Burn Rate Indicator */}
        {hourlyCost > 2 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mt-2 pt-2 border-t border-gray-800/50"
            transition={{ duration: 0.3 }}
          >
            <div className="flex items-center justify-center space-x-1">
              <div className="h-1.5 w-1.5 bg-yellow-500/60 animate-pulse" />
              <span className="text-xs text-yellow-500/80">High burn rate</span>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default InstanceCostMeter;